import check from '../libraries/check-types-ext';
import AppError from '../libraries/AppError';
import * as uuid from 'uuid';
import * as utils from '../libraries/Utils';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import ProductCatalog from '../products/ProductCatalog';
import OrderLine2, { OrderLineDriveInDefinition, OrderLineHeatBoxDefinition, OrderLineHomeDeliveryDefinition, OrderLineType, } from './OrderLine2';
import ProductSelectionG4 from './OrderLineMappings';
import mapToOrderLinesG5 from './OrderLineMappingsG5';
import getLuxonImpl from '../libraries/timezone/timeboxImpl';
import { shouldApplyDiscount } from './OrderLineMappingHelper';
import { ProductSelectionType, } from './OrderTypes';
import { discountTypeEnum } from '../types/EnumTypes';
const setupWarningMessagePrefix = (group, prefix1, prefix2) => {
    return group.options.minQuantity === group.options.maxQuantity ? prefix1 : prefix2;
};
const getSelectedProductWithPrice = (group, selectedProduct) => {
    const prices = group.prices || {};
    if (typeof selectedProduct === 'string') {
        return Object.assign({ productId: selectedProduct }, prices[selectedProduct]);
    }
    return Object.assign(Object.assign({}, selectedProduct), prices[selectedProduct.productId]);
};
export const extractProductsFromProductSelection = (productSelection) => {
    if (!Array.isArray(productSelection.groups)) {
        return [productSelection];
    }
    return productSelection.groups.reduce((carry, group) => {
        if (!Array.isArray(group.selectedProducts)) {
            return carry;
        }
        return [
            ...carry,
            ...group.selectedProducts.reduce((acc, selectedProduct) => {
                if (typeof selectedProduct === 'string') {
                    return [...acc, getSelectedProductWithPrice(group, selectedProduct)];
                }
                if (!selectedProduct.productId) {
                    return acc;
                }
                return [...acc, getSelectedProductWithPrice(group, selectedProduct)];
            }, []),
        ];
    }, [productSelection]);
};
export const mapProductSelection = (productCatalog, productSelections = [], singleProductFn) => {
    return productSelections.map((selection) => {
        const { groups } = selection;
        if (groups) {
            const isMenuProduct = selection.productId ? utils.isMenu(productCatalog, selection.productId) : false;
            const newGroups = groups.map((group) => {
                if (isMenuProduct) {
                    const { selectedProducts } = group;
                    const mappedSelections = mapProductSelection(productCatalog, selectedProducts, singleProductFn);
                    return Object.assign(Object.assign({}, group), { selectedProducts: mappedSelections });
                }
                else {
                    const mappedSelections = singleProductFn(group.selectedProducts);
                    return Object.assign(Object.assign({}, group), { selectedProducts: mappedSelections });
                }
            });
            return Object.assign(Object.assign({}, selection), { groups: newGroups });
        }
        else {
            return selection;
        }
    });
};
export default class ProductSelection {
    constructor(selection, processGeneration = DomainCoreGeneration.Generation1, rootProductId = null) {
        this.selectionRoot = selection;
        this.generation = processGeneration;
        this.rootProductId = rootProductId;
    }
    static extractQuantityFromProductSelection(productSelection) {
        if (productSelection.quantity !== undefined) {
            return productSelection.quantity;
        }
        else if (Array.isArray(productSelection.groups)) {
            return productSelection.groups.reduce((carry, group) => {
                if (Array.isArray(group.selectedProducts)) {
                    return (carry +
                        group.selectedProducts.reduce((carry, selectedProduct) => {
                            if (selectedProduct.quantity !== undefined) {
                                return carry + selectedProduct.quantity;
                            }
                            return carry;
                        }, 0));
                }
                return carry;
            }, 0);
        }
        else {
            return 0;
        }
    }
    static createFromProduct(product, options = {}) {
        const TimeBox = getLuxonImpl().timebox;
        const selection = {
            productSelectionId: uuid.v4(),
            productId: product.id,
            price: product.options.noMainProduct !== true ? product.price : undefined,
            configurations: [],
            groups: [],
            quantity: undefined,
        };
        if (product.options.expiration) {
            selection.expiration = {
                expirationTime: (options.createdAtTimestamp ? options.createdAtTimestamp : TimeBox.realNow().unix().exec()) +
                    product.options.expiration,
                expirationReason: 'productDefinedExpiration',
            };
        }
        else if (options.expiration) {
            const checkIfHasValues = (obj) => {
                const emptyValues = ['', null, undefined];
                const values = Object.values(obj).filter((val) => !emptyValues.includes(val));
                return values.length > 0;
            };
            const expiration = options.expiration.tenantSettings && checkIfHasValues(options.expiration.tenantSettings)
                ? options.expiration.tenantSettings
                : options.expiration.tenantConfig;
            const timeToExpire = product.options && product.options.type ? expiration[product.options.type] : null;
            if (timeToExpire) {
                selection.expiration = {
                    expirationTime: (options.expiration.createdAtTimestamp
                        ? options.expiration.createdAtTimestamp
                        : TimeBox.realNow().unix().exec()) + timeToExpire,
                    expirationReason: `tenantDefinedExpiration-${product.options.type}`,
                };
            }
        }
        if (!product.options.useProductsInGroup) {
            selection.quantity = 0;
        }
        if (product.groups) {
            selection.groups = [];
            for (const i in product.groups) {
                const group = product.groups[i];
                const selectionGroup = {
                    name: undefined,
                    description: undefined,
                    id: uuid.v4(),
                    groupId: group.id,
                    selectedProducts: [],
                    options: group.options,
                    appearance: group.appearance,
                };
                if (group.options.defaultSelected) {
                    selectionGroup.selectedProducts = [group.options.defaultSelected];
                }
                if (group.options.showIfConfigurationOptionSelected) {
                    const parentConfig = product.configurations.find((cp) => {
                        return cp.id === group.options.showIfConfigurationOptionSelected.configurationId;
                    });
                    if (parentConfig) {
                        if (typeof parentConfig.options.defaultSelected !== 'undefined') {
                            if (typeof group.options.showIfConfigurationOptionSelected.optionIndex !== 'undefined') {
                                if (parentConfig.options.defaultSelected === group.options.showIfConfigurationOptionSelected.optionIndex) {
                                    selection.groups.push(selectionGroup);
                                }
                            }
                            else {
                                throw AppError.ConditionalGroupMustHaveParentIndex();
                            }
                        }
                        else {
                            throw AppError.ConditionalGroupParentMustHaveSelected();
                        }
                    }
                    else {
                        throw AppError.ConditionalGroupMustHaveParent();
                    }
                }
                else {
                    selection.groups.push(selectionGroup);
                }
            }
        }
        if (product.configurations) {
            selection.configurations = [];
            for (const i in product.configurations) {
                const config = product.configurations[i];
                const selectedValue = (() => {
                    if (typeof config.options.defaultSelected !== 'undefined') {
                        return config.options.defaultSelected;
                    }
                    if (typeof config.options.defaultSelectedArray !== 'undefined') {
                        return config.options.defaultSelectedArray;
                    }
                })();
                const selectionConfig = {
                    configurationId: config.id,
                    selectedValue: selectedValue,
                };
                selection.configurations.push(selectionConfig);
            }
        }
        return selection;
    }
    static hasHeatableProduct(productCatalog, selections, includeDisabledProducts = false) {
        let found = false;
        for (const i in selections) {
            const selection = selections[i];
            const product = productCatalog.getProduct(selection.productId, includeDisabledProducts);
            if (product && product.heatable) {
                found = true;
                break;
            }
        }
        return found;
    }
    static mapToOrderLines(productCatalog, selections, includeHeatBoxes = false, options = {}, generation = DomainCoreGeneration.Generation1, orderTags = [], topLevel = false) {
        check.boolean.orFail(includeHeatBoxes, 'heatboxes');
        check.object.orFail(options, 'options');
        check.number.orFail(generation, 'generation');
        check.instanceOf(ProductCatalog).orFail(productCatalog, 'productCatalog');
        check.array.of.object.orFail(selections, 'selections');
        const orderLines = [];
        selections.forEach((selection) => {
            new ProductSelection(selection, generation, selection.productId)
                .mapToOrderLines(productCatalog, !!options.includeDisabledProducts, false, orderTags)
                .forEach((line) => {
                orderLines.push(line);
            });
        });
        if ((includeHeatBoxes &&
            ProductSelection.hasHeatableProduct(productCatalog, selections, !!options.includeDisabledProducts)) ||
            (includeHeatBoxes && options.isAdmin)) {
            orderLines.push(OrderLineHeatBoxDefinition);
        }
        if (topLevel) {
            const deliveryChoice = options.deliveryChoice;
            if (deliveryChoice === 2 && typeof options.driveInDelivery === 'object' && options.driveInDelivery !== null) {
                const { name = OrderLineDriveInDefinition.name, price = 0 } = options.driveInDelivery;
                orderLines.push(new OrderLine2({
                    name,
                    price,
                    type: OrderLineDriveInDefinition.type,
                    ean: OrderLineDriveInDefinition.ean,
                    unitType: OrderLineDriveInDefinition.unitType,
                    quantity: OrderLineDriveInDefinition.quantity,
                }));
            }
            if (deliveryChoice === 3) {
                orderLines.push(new OrderLine2({
                    name: OrderLineHomeDeliveryDefinition.name,
                    price: OrderLineHomeDeliveryDefinition.price,
                    type: OrderLineHomeDeliveryDefinition.type,
                    ean: OrderLineHomeDeliveryDefinition.ean,
                    unitType: OrderLineHomeDeliveryDefinition.unitType,
                    quantity: OrderLineHomeDeliveryDefinition.quantity,
                }));
            }
            const { priceRules: filteredRules } = productCatalog;
            const filterBasedOnVoucher = (rule) => shouldApplyDiscount(rule, {
                vouchers: options.vouchers,
            });
            const globalRules = filteredRules.filter((rule) => !rule.productSpecific && rule.discountType === discountTypeEnum.globalDiscountWithMax);
            const computedGlobalPriceRules = globalRules.find(filterBasedOnVoucher);
            const lineBasePrice = (line) => {
                if (line.type === 'Product') {
                    const quantity = line.quantity ? line.quantity : 1;
                    return line.totalPrice ? line.totalPrice : line.price * quantity;
                }
                else if (line.type === 'Aggregation') {
                    return line.subLines.reduce((acc, cur) => {
                        return acc + lineBasePrice(cur);
                    }, 0);
                }
                else {
                    return 0;
                }
            };
            if (computedGlobalPriceRules) {
                const perc = computedGlobalPriceRules.options.discountPct;
                const discountSum = orderLines.reduce((acc, cur) => {
                    return acc + lineBasePrice(cur) * perc;
                }, 0);
                const roundedDiscountSum = Math.round(discountSum * 100) / 100;
                orderLines.push(new OrderLine2({
                    name: 'Total Rabat',
                    subLines: [],
                    type: OrderLineType.Discount,
                    quantity: 1,
                    unitType: '',
                    price: -1 * roundedDiscountSum,
                }));
            }
            if (options.promoCode && options.employeeDiscount && options.employeeDiscount.active) {
                const perc = options.employeeDiscount.percent || 0;
                const discountSum = orderLines.reduce((acc, cur) => {
                    return acc + (lineBasePrice(cur) * perc) / 100;
                }, 0);
                const roundedDiscountSum = Math.round(discountSum * 100) / 100;
                orderLines.push(new OrderLine2({
                    name: 'Total Rabat',
                    subLines: [],
                    type: OrderLineType.Discount,
                    quantity: 1,
                    unitType: '',
                    price: -1 * roundedDiscountSum,
                }));
            }
            if (options.loyaltySystemDiscount) {
                const roundedDiscountSum = options.loyaltySystemDiscount.discountAmount;
                orderLines.push(new OrderLine2({
                    name: 'Total Rabat',
                    subLines: [],
                    type: OrderLineType.Discount,
                    quantity: 1,
                    unitType: '',
                    price: -roundedDiscountSum,
                }));
            }
        }
        return orderLines;
    }
    static mapToOrderLinesGx(bmId) {
        return function (productCatalog, selections, includeHeatBoxes, options, orderTags = []) {
            const generation = DomainCoreGeneration.getGenerationByBMId(bmId);
            if (generation === DomainCoreGeneration.Generation5) {
                return mapToOrderLinesG5(productCatalog, selections, options, orderTags);
            }
            else if (generation === DomainCoreGeneration.Generation4) {
                return ProductSelectionG4.mapToOrderLinesG4(productCatalog, selections, orderTags);
            }
            else if (generation === DomainCoreGeneration.Generation3) {
                throw new Error('Should never get here as G3 is phased out now.');
            }
            else {
                return ProductSelection.mapToOrderLines(productCatalog, selections, includeHeatBoxes, options, DomainCoreGeneration.Generation1, orderTags, true);
            }
        };
    }
    static mapToOrderLinesGen(generation) {
        return function (productCatalog, selections, includeHeatBoxes, options, orderTags = [], includedPriceDisabled = false, discountTypeDisable = []) {
            if (generation === DomainCoreGeneration.Generation5) {
                return mapToOrderLinesG5(productCatalog, selections, options, orderTags, includedPriceDisabled, discountTypeDisable);
            }
            if (generation === DomainCoreGeneration.Generation4) {
                return ProductSelectionG4.mapToOrderLinesG4(productCatalog, selections, orderTags);
            }
            else if (generation === DomainCoreGeneration.Generation3) {
                throw new Error('Should never get here as G3 is phased out now.');
            }
            else {
                return ProductSelection.mapToOrderLines(productCatalog, selections, includeHeatBoxes, options, DomainCoreGeneration.Generation1, orderTags, true);
            }
        };
    }
    static mapToOrderLine(productCatalog, selection, isSubline = false, orderTags = [], rootProductId = null) {
        check.instanceOf(ProductCatalog).orFail(productCatalog, 'productCatalog');
        check.object.orFail(selection, 'selection');
        return new ProductSelection(selection, DomainCoreGeneration.Generation1, rootProductId).mapToOrderLines(productCatalog, false, isSubline, orderTags)[0];
    }
    mapToOrderLines(productCatalog, includeDisabledProducts = false, isSubline = false, orderTags = []) {
        if (DomainCoreGeneration.Generation1 === this.generation) {
            return this.mapToOrderLinesG1(productCatalog, includeDisabledProducts, isSubline);
        }
        else if (DomainCoreGeneration.Generation3 === this.generation) {
            throw new Error('Should never get here as G3 is phased out now.');
        }
        else if (DomainCoreGeneration.Generation4 === this.generation) {
            return ProductSelectionG4.mapToOrderLinesG4(productCatalog, [this.selectionRoot], orderTags);
        }
        else if (DomainCoreGeneration.Generation5 === this.generation) {
            return mapToOrderLinesG5(productCatalog, [this.selectionRoot], orderTags);
        }
        else {
            throw new Error('NotImplemented');
        }
    }
    mapToOrderLinesG1(productCatalog, includeDisabledProducts = false, isSubline = false) {
        const rootProduct = this.selectionRoot.type !== ProductSelectionType.Config
            ? productCatalog.getProduct(this.selectionRoot.productId, includeDisabledProducts)
            : undefined;
        let subLinesForMainProduct = [];
        let subLinesForAddons = [];
        if (this.selectionRoot.groups) {
            const predicate = (g) => {
                const groupObj = rootProduct.getGroup(g.groupId);
                return groupObj && groupObj.options.useInGroupOnly;
            };
            const predicate2 = (g) => {
                if (rootProduct.options.mainProductFromGroupId !== undefined)
                    return g.groupId !== rootProduct.options.mainProductFromGroupId;
                else
                    return true;
            };
            const groupsConfiguringMainProduct = this.selectionRoot.groups.filter((g) => predicate(g) && predicate2(g));
            const groupsRepresentingAddons = this.selectionRoot.groups.filter((g) => !predicate(g) && predicate2(g));
            subLinesForMainProduct = groupsConfiguringMainProduct
                .map((groupSelection) => this._createSubLinesFromGroupSelection(productCatalog, rootProduct, groupSelection, this.selectionRoot))
                .filter((subLine) => subLine instanceof OrderLine2);
            subLinesForAddons = groupsRepresentingAddons
                .map((groupSelection) => this._createSubLinesFromGroupSelection(productCatalog, rootProduct, groupSelection, this.selectionRoot))
                .filter((subLine) => subLine instanceof OrderLine2);
            check.array.orFail(subLinesForMainProduct);
        }
        const subLinesForConfiguration = this.selectionRoot.configurations
            ? this.selectionRoot.configurations.map((configSelection) => this._createSubLineFromConfigurationSelection(productCatalog, rootProduct, configSelection))
            : [];
        const mainProduct = rootProduct ? this._getMainProduct(productCatalog, rootProduct) : undefined;
        const result = [];
        if (mainProduct) {
            check.string.orFail(mainProduct.unit, 'unitType');
            const price = isSubline ? mainProduct.priceStructure.priceInGroup : mainProduct.price;
            const extraSubLines = subLinesForConfiguration;
            const mainLine = new OrderLine2({
                type: OrderLineType.Product,
                productId: mainProduct.id,
                rootProductId: this.rootProductId,
                quantity: this.selectionRoot.quantity,
                discount: isSubline ? undefined : rootProduct.discount,
                voucherId: this.selectionRoot.voucherId,
                name: mainProduct.name,
                options: rootProduct.options,
                ean: mainProduct.ean,
                plu: mainProduct.plu,
                addedByGame: this.selectionRoot.addedByGame,
                price,
                unitType: mainProduct.unit,
                subLines: [...extraSubLines, ...subLinesForMainProduct],
            });
            result.push(mainLine);
        }
        subLinesForAddons.forEach((line) => {
            if (line.name === undefined || line.name === '') {
                if (line.subLines) {
                    line.subLines.forEach((subline) => {
                        result.push(subline);
                    });
                }
            }
            else {
                result.push(line);
            }
        });
        if (result.length > 1 || (rootProduct && rootProduct.options.noMainProduct && result.length > 0)) {
            check.string.orFail(rootProduct.id);
            const line2 = new OrderLine2({
                type: OrderLineType.Aggregation,
                name: rootProduct.name,
                discount: rootProduct.discount,
                quantity: this.selectionRoot.quantity,
                options: rootProduct.options,
                addedByGame: this.selectionRoot.addedByGame,
                rootProductId: this.rootProductId ? this.rootProductId : rootProduct.id,
                subLines: [...result, ...subLinesForConfiguration],
            });
            return [line2];
        }
        else
            return result;
    }
    _getMainProduct(productCatalog, rootProduct) {
        const mainProductFromGroupId = rootProduct.options.mainProductFromGroupId;
        if (mainProductFromGroupId !== undefined) {
            if (!this.selectionRoot.groups) {
                throw AppError.MainProductMissingError().addData('rootProduct', rootProduct);
            }
            const groupSelection = this.selectionRoot.groups.find((g) => g.groupId === mainProductFromGroupId);
            const ps = groupSelection.selectedProducts;
            check.array.orFail(ps);
            if (ps.length !== 1) {
                throw AppError.CodingError().addData('selectedProducts', ps);
            }
            const productId = typeof ps[0] === 'string' ? ps[0] : ps[0].productId;
            return productCatalog.getProduct(productId);
        }
        else {
            if (rootProduct.options.noMainProduct)
                return undefined;
            else
                return rootProduct;
        }
    }
    _createSubLineFromConfigurationSelection(productCatalog, rootProduct, configSelection) {
        const config = rootProduct.getConfig(configSelection.configurationId);
        const orderText = (() => {
            switch (config.appearance) {
                case 'switches': {
                    const val = parseInt(configSelection.selectedValue ? configSelection.selectedValue : 0);
                    check.number.orFail(val);
                    check.array.of.object.orFail(config.options.options, 'configurations.options');
                    const option = config.options.options.find((opt) => opt.id === val);
                    check.object.orFail(option, 'option', config.options.options);
                    return config.name + ': ' + option.name;
                }
                case 'textarea':
                    return config.name + ': ' + configSelection.selectedValue;
                case 'checklist':
                    return config.name;
            }
        })();
        let copiedOptions;
        let unselectedOption;
        const hasSelectedValue = typeof configSelection.selectedValue !== 'undefined';
        const _options = config.options.options;
        const hasOptions = Array.isArray(_options);
        const hasMultipleSelectedValues = Array.isArray(configSelection.selectedValue);
        if (hasSelectedValue && hasOptions) {
            if (hasMultipleSelectedValues) {
                const _selectedOption = _options.filter((opt, index) => {
                    return configSelection.selectedValue.indexOf(index) !== -1;
                });
                const _unselectedOption = _options.filter((opt, index) => {
                    return configSelection.selectedValue.indexOf(index) === -1;
                });
                copiedOptions = _selectedOption.map((op) => utils.copyObject(op));
                unselectedOption = _unselectedOption.map((op) => utils.copyObject(op));
            }
            else {
                copiedOptions = utils.copyObject(_options[configSelection.selectedValue]);
                const _unselectedOption = _options.filter((opt, index) => {
                    return configSelection.selectedValue === index;
                });
                unselectedOption = _unselectedOption.map((op) => utils.copyObject(op));
            }
        }
        return new OrderLine2({
            name: orderText,
            type: OrderLineType.Config,
            selectedOption: copiedOptions,
            unselectedOption: unselectedOption || undefined,
        });
    }
    _firstval(val1 = undefined, val2 = undefined, val3 = undefined) {
        if (val1 !== undefined)
            return val1;
        if (val2 !== undefined)
            return val2;
        if (val3 !== undefined)
            return val3;
        else
            return undefined;
    }
    _firstvalString(val1, val2 = undefined, val3 = undefined) {
        if (typeof val1 === 'string' && val1)
            return val1;
        if (typeof val2 === 'string' && val2)
            return val2;
        if (typeof val3 === 'string' && val3)
            return val3;
        else
            return undefined;
    }
    _createSubLinesFromGroupSelection(productCatalog, rootProduct, groupSelection, selectionRoot) {
        if (!check.array.of.string(groupSelection.selectedProducts) &&
            !check.array.of.object(groupSelection.selectedProducts)) {
            throw AppError.CodingError('Expected array of numbers or objects').addData('selectedProducts', groupSelection.selectedProducts);
        }
        const myGroup = rootProduct.getGroup(groupSelection.groupId);
        const productValueObjects = groupSelection.selectedProducts.map((selectedProduct) => {
            const { productId, quantity } = (() => {
                if (check.string(selectedProduct)) {
                    return {
                        productId: selectedProduct,
                        quantity: undefined,
                    };
                }
                else {
                    check.string.orFail(selectedProduct.productId);
                    return {
                        productId: selectedProduct.productId,
                        quantity: selectedProduct.quantity,
                    };
                }
            })();
            if (typeof selectedProduct === 'object' &&
                ((Array.isArray(selectedProduct.groups) && selectedProduct.groups.length > 0) ||
                    (Array.isArray(selectedProduct.configurations) && selectedProduct.configurations.length > 0))) {
                if (typeof selectedProduct.quantity === 'undefined') {
                    selectedProduct.quantity = this.selectionRoot.quantity;
                }
                return {
                    OrderLine: ProductSelection.mapToOrderLine(productCatalog, selectedProduct, true, [], this.rootProductId),
                };
            }
            const pdFromCatalog = productCatalog.getProduct(productId);
            const pdFromGroup = myGroup.products.find((p) => p.id_ref === productId);
            if (pdFromGroup) {
                const prFromGrp = parseFloat(pdFromGroup.productPriceInGroup);
                if (!isNaN(prFromGrp)) {
                    pdFromCatalog.priceStructure.priceInGroup = prFromGrp;
                }
            }
            const productName = (() => {
                var _a;
                if (((_a = groupSelection.options) === null || _a === void 0 ? void 0 : _a.withTextArea) && selectedProduct.textValue) {
                    return `${pdFromCatalog.name} (${selectedProduct.textValue})`;
                }
                return pdFromCatalog.name;
            })();
            return {
                id: productId,
                name: productName,
                price: pdFromCatalog.priceStructure.priceInGroup,
                unit: pdFromCatalog.unit,
                ean: pdFromCatalog.ean,
                plu: pdFromCatalog.plu,
                quantity: quantity,
                options: myGroup.options,
            };
        });
        const coef = myGroup.options.countPerAmount && selectionRoot && selectionRoot.quantity ? selectionRoot.quantity : 1;
        let freeQuantityCount = myGroup.options.freeQuantity !== undefined ? parseInt(myGroup.options.freeQuantity) * coef : 0;
        let overFreeQuantityCount = 0;
        const subLines = productValueObjects.reduce((pv, vo) => {
            if (vo.OrderLine instanceof OrderLine2) {
                return [...pv, vo.OrderLine];
            }
            else {
                const data = {
                    productId: vo.id,
                    rootProductId: rootProduct.id,
                    name: vo.name,
                };
                if (myGroup.options.freeQuantity !== undefined &&
                    myGroup.options.maxQuantity !== undefined &&
                    myGroup.options.freeQuantity === myGroup.options.maxQuantity) {
                    data.type = OrderLineType.Config;
                    if (typeof vo.quantity === 'number') {
                        data.quantity = vo.quantity;
                    }
                }
                else {
                    data.type = OrderLineType.Product;
                    data.options = vo.options;
                    data.price = this._firstval(vo.price);
                    data.unitType = this._firstvalString(vo.unit, myGroup.unit, rootProduct.unit);
                    data.ean = this._firstval(myGroup.ean, vo.ean);
                    data.plu = this._firstval(myGroup.plu, vo.plu);
                    data.quantity = this._firstval(vo.quantity, this.selectionRoot.quantity);
                    if (freeQuantityCount > 0) {
                        data.includedInMainPrice = true;
                        const usedQty = myGroup.options.countPerAmount ? data.quantity : 1;
                        if (freeQuantityCount < usedQty) {
                            overFreeQuantityCount = usedQty - freeQuantityCount;
                            data.quantity = freeQuantityCount;
                            freeQuantityCount = 0;
                        }
                        else {
                            freeQuantityCount = freeQuantityCount - usedQty;
                        }
                    }
                    else {
                        data.includedInMainPrice = false;
                    }
                }
                if (overFreeQuantityCount > 0) {
                    const extraData = Object.assign({}, data);
                    extraData.includedInMainPrice = false;
                    extraData.quantity = overFreeQuantityCount;
                    overFreeQuantityCount = 0;
                    return [...pv, new OrderLine2(data), new OrderLine2(extraData)];
                }
                return [...pv, new OrderLine2(data)];
            }
        }, []);
        if (subLines.length === 1 && !rootProduct.options.noMainProduct) {
            if (myGroup.products && myGroup.products.length > 1) {
                const emptyValues = [null, undefined, '', ' '];
                const prefix = emptyValues.includes(myGroup.name) ? '' : `${myGroup.name} - `;
                subLines[0].name = prefix + subLines[0].name;
            }
            return subLines[0];
        }
        else if (subLines.length > 0) {
            return new OrderLine2({
                name: myGroup.name,
                type: (() => {
                    if (subLines[0].type === OrderLineType.Config) {
                        return OrderLineType.Config;
                    }
                    else if ([OrderLineType.Product, OrderLineType.Aggregation].includes(subLines[0].type)) {
                        return OrderLineType.Aggregation;
                    }
                    else {
                        throw AppError.CodingError('Should not happen');
                    }
                })(),
                rootProductId: rootProduct.id,
                subLines: subLines,
            });
        }
        return null;
    }
    static getGroupMinimumQuantityInfo({ group, productSelection }) {
        if (group.options.customWarningMinimum !== undefined) {
            return group.options.customWarningMinimum;
        }
        if (group.options.minQuantity !== undefined) {
            const groupQuantityPerProductQuantity = group.options.groupQuantityPerProductQuantity &&
                typeof productSelection.quantity === 'number' &&
                productSelection.quantity > 0;
            const minQuantity = groupQuantityPerProductQuantity
                ? group.options.minQuantity * productSelection.quantity
                : group.options.minQuantity;
            const priceUnit = group.options.priceUnit ? group.options.priceUnit : 'stk.';
            return setupWarningMessagePrefix(group, 'Vælg ', 'Vælg minimum ') + minQuantity + ' ' + priceUnit;
        }
        return null;
    }
    static getGroupMaximumQuantityInfo({ group, productSelection }) {
        if (group.options.customWarningMaximum !== undefined) {
            return group.options.customWarningMaximum;
        }
        if (group.options.maxQuantity !== undefined) {
            const groupQuantityPerProductQuantity = group.options.groupQuantityPerProductQuantity &&
                typeof productSelection.quantity === 'number' &&
                productSelection.quantity > 0;
            const maxQuantity = groupQuantityPerProductQuantity
                ? group.options.maxQuantity * productSelection.quantity
                : group.options.maxQuantity;
            const priceUnit = group.options.priceUnit ? group.options.priceUnit : 'stk.';
            return setupWarningMessagePrefix(group, 'Vælg ', 'Vælg maximum ') + maxQuantity + ' ' + priceUnit;
        }
        return null;
    }
    static getGroupSum({ product, selectedGroup, group }) {
        if (product.options.useProductsInGroup || group.options.countPerAmount) {
            return selectedGroup.selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
        }
        else {
            return selectedGroup.selectedProducts.length;
        }
    }
    static getFreeQuantityFulfilled({ group, product, productSelection }) {
        const selectedGroup = productSelection.groups.find((psg) => psg.groupId === group.id);
        if (selectedGroup !== undefined) {
            const groupSum = ProductSelection.getGroupSum({
                product,
                group,
                selectedGroup,
            });
            const productQuantity = group.options.countPerAmount ? productSelection.quantity : 1;
            if (typeof productSelection.quantity === 'number' &&
                group.options.freeQuantity !== undefined &&
                groupSum >= group.options.freeQuantity * productQuantity) {
                return true;
            }
        }
        return false;
    }
    static getMaxQuantityFulfilled({ group, product, productSelection }) {
        const selectedGroup = productSelection.groups.find((psg) => psg.groupId === group.id);
        if (selectedGroup !== undefined) {
            const groupSum = ProductSelection.getGroupSum({
                product,
                group,
                selectedGroup,
            });
            const groupQuantityPerProductQuantity = group.options.groupQuantityPerProductQuantity &&
                typeof productSelection.quantity === 'number' &&
                productSelection.quantity > 0;
            const maxQuantity = groupQuantityPerProductQuantity
                ? group.options.maxQuantity * productSelection.quantity
                : group.options.maxQuantity;
            return groupSum >= maxQuantity;
        }
        return false;
    }
    static validateGroup({ group, productSelection, product }) {
        if (group.products) {
            const selectedGroup = productSelection.groups.find((productSelectionGroup) => productSelectionGroup.groupId === group.id);
            if (selectedGroup === undefined) {
                if (typeof group.options.showIfConfigurationOptionSelected !== 'object') {
                    productSelection.warning = `Group ${group.name} is missing from product selection!`;
                    return false;
                }
            }
            else {
                const groupSum = ProductSelection.getGroupSum({
                    product,
                    group,
                    selectedGroup,
                });
                const warningObject = (() => {
                    const groupQuantityPerProductQuantity = group.options.groupQuantityPerProductQuantity &&
                        typeof productSelection.quantity === 'number' &&
                        productSelection.quantity > 0;
                    const priceUnit = group.options.priceUnit ? group.options.priceUnit : 'stk.';
                    if (group.options.minQuantity !== undefined) {
                        const minQuantity = groupQuantityPerProductQuantity
                            ? group.options.minQuantity * productSelection.quantity
                            : group.options.minQuantity;
                        if (groupSum < minQuantity) {
                            return {
                                type: 'invalidMinQuantity',
                                warningMessage: setupWarningMessagePrefix(group, 'Vælg ', 'Vælg minimum ') + minQuantity + ' ' + priceUnit,
                            };
                        }
                    }
                    if (group.options.maxQuantity !== undefined) {
                        const maxQuantity = groupQuantityPerProductQuantity
                            ? group.options.maxQuantity * productSelection.quantity
                            : group.options.maxQuantity;
                        if (groupSum > maxQuantity) {
                            return {
                                type: 'invalidMaxQuantity',
                                warningMessage: setupWarningMessagePrefix(group, 'Vælg ', 'Vælg maximum ') + maxQuantity + ' ' + priceUnit,
                            };
                        }
                    }
                })();
                if (warningObject) {
                    selectedGroup.warning = warningObject.warningMessage;
                    selectedGroup.warningObject = warningObject;
                    return false;
                }
                else {
                    selectedGroup.warning = undefined;
                    selectedGroup.warningObject = {};
                }
            }
        }
        return true;
    }
    static validateConfig({ config, productSelection, product }) {
        const selectedConfig = productSelection.configurations.find((productSelectionGroup) => productSelectionGroup.configurationId === config.id);
        const productConfig = product.configurations.find((cfg) => cfg.id === config.id);
        if (selectedConfig === undefined || productConfig === undefined) {
            return false;
        }
        else {
            if (config.options.options) {
                const optionSum = config.options.options.reduce(function (s, o) {
                    return s + (o.selected ? 1 : o.amount || 0);
                }, 0);
                if (config.options.minQuantity !== undefined &&
                    optionSum < config.options.minQuantity * productSelection.quantity) {
                    return false;
                }
                if (config.options.maxQuantity !== undefined &&
                    optionSum > config.options.maxQuantity * productSelection.quantity) {
                    return false;
                }
                if (selectedConfig.selectedValue === undefined) {
                    return false;
                }
            }
        }
        return true;
    }
    static validate(product, productSelection) {
        check.object.orFail(product);
        check.object.orFail(productSelection);
        let foundError = false;
        if (productSelection) {
            if (productSelection.quantity < (product.minQuantity || 1)) {
                foundError = true;
                productSelection.warning = 'Vælg minimum ' + product.minQuantity + ' stk.';
            }
            else {
                productSelection.warning = undefined;
            }
            if (product.configurations) {
                for (const i in product.configurations) {
                    const config = product.configurations[i];
                    const selectedConfig = productSelection.configurations[i];
                    let configInvalidMessage = undefined;
                    if (config.options.options) {
                        const optionSum = config.options.options.reduce(function (s, o) {
                            return s + (o.selected ? 1 : o.amount || 0);
                        }, 0);
                        if (config.options.minQuantity !== undefined &&
                            optionSum < config.options.minQuantity * productSelection.quantity) {
                            configInvalidMessage = 'Vælg minimum ' + config.options.minQuantity + ' stk.';
                        }
                        if (config.options.maxQuantity !== undefined &&
                            optionSum > config.options.maxQuantity * productSelection.quantity) {
                            configInvalidMessage = 'Vælg maximum ' + config.options.maxQuantity + ' stk.';
                        }
                        if (selectedConfig.selectedValue === undefined) {
                            configInvalidMessage = config.options.customWarning
                                ? config.options.customWarning
                                : 'Vælg kagemand eller kagekone';
                        }
                        if (configInvalidMessage) {
                            foundError = true;
                        }
                        selectedConfig.warning = configInvalidMessage;
                    }
                }
            }
            if (product.groups) {
                if (Array.isArray(productSelection.groups)) {
                    const groupIds = productSelection.groups.map((group) => {
                        return group.groupId;
                    });
                    const arrayHasDuplicates = (array) => new Set(array).size !== array.length;
                    if (arrayHasDuplicates(groupIds)) {
                        foundError = true;
                    }
                }
                product.groups.forEach((group) => {
                    foundError =
                        foundError ||
                            ProductSelection.validateGroup({
                                group,
                                product,
                                productSelection,
                            }) !== true;
                });
            }
        }
        return foundError;
    }
    static ChangeQuantity(productSelection, product, quantity) {
        const minunitComputed = product.minQuantity || 0;
        if (quantity > productSelection.quantity) {
            if (quantity < minunitComputed) {
                productSelection.quantity = minunitComputed;
            }
            else {
                productSelection.quantity = quantity;
            }
        }
        else {
            if (quantity < minunitComputed && minunitComputed > 1) {
                productSelection.quantity = 0;
                return minunitComputed > 1;
            }
            else {
                productSelection.quantity = quantity;
            }
        }
        return false;
    }
    static changeQuantity(productSelection, product, quantity) {
        const minunitComputed = product.minQuantity || 0;
        if (quantity > productSelection.quantity) {
            if (quantity < minunitComputed) {
                productSelection.quantity = minunitComputed;
            }
            else {
                productSelection.quantity = quantity;
            }
        }
        else {
            if (quantity < minunitComputed && minunitComputed > 1) {
                productSelection.quantity = 0;
                return minunitComputed > 1;
            }
            else {
                productSelection.quantity = quantity;
            }
        }
        return productSelection;
    }
}
